/**
 * AttributeManager Component
 * Modal for managing flight attributes (create, edit, delete, reorder)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlightAttributesStore, FlightAttribute, ATTRIBUTE_COLORS, ATTRIBUTE_ICONS, generateSlug } from '../store/flightAttributes';
import { ColorPicker } from './ColorPicker';

// Icon component that renders Heroicons based on icon name
function AttributeIcon({ icon, className = 'w-4 h-4' }: { icon: string; className?: string }) {
  const icons: Record<string, JSX.Element> = {
    'document-text': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    'currency-euro': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 15.536c-1.171 1.952-3.07 1.952-4.242 0-1.172-1.953-1.172-5.119 0-7.072 1.171-1.952 3.07-1.952 4.242 0M8 10.5h4m-4 3h4m9-1.5a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    'megaphone': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    ),
    'sparkles': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
    'fire': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
      </svg>
    ),
    'heart': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
    'star': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    ),
    'bolt': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    'calendar': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    'camera': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    'video-camera': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    'music-note': (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
      </svg>
    ),
  };

  return icons[icon] || null;
}

interface AttributeManagerProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function AttributeManager({ projectId, isOpen, onClose }: AttributeManagerProps) {
  const { t } = useTranslation(['flights', 'common']);
  const {
    attributes,
    error,
    fetchAttributes,
    createAttribute,
    updateAttribute,
    deleteAttribute,
    clearError,
  } = useFlightAttributesStore();

  const [editingAttribute, setEditingAttribute] = useState<FlightAttribute | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: ATTRIBUTE_COLORS[0],
    icon: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Load attributes when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      fetchAttributes(projectId);
    }
  }, [isOpen, projectId, fetchAttributes]);

  // Reset form when opening create mode
  useEffect(() => {
    if (isCreating) {
      // Pick a random color that's not already used
      const usedColors = attributes.map(a => a.color);
      const availableColors = ATTRIBUTE_COLORS.filter(c => !usedColors.includes(c));
      const nextColor = availableColors.length > 0 ? availableColors[0] : ATTRIBUTE_COLORS[0];

      setFormData({
        name: '',
        description: '',
        color: nextColor,
        icon: '',
      });
    }
  }, [isCreating, attributes]);

  // Populate form when editing
  useEffect(() => {
    if (editingAttribute) {
      setFormData({
        name: editingAttribute.name,
        description: editingAttribute.description || '',
        color: editingAttribute.color,
        icon: editingAttribute.icon || '',
      });
    }
  }, [editingAttribute]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLocalError(null);

    try {
      if (isCreating) {
        await createAttribute(projectId, {
          name: formData.name,
          slug: generateSlug(formData.name),
          description: formData.description || undefined,
          color: formData.color,
          icon: formData.icon || undefined,
        });
        setIsCreating(false);
      } else if (editingAttribute) {
        // System attributes: only allow color/icon changes
        const updateData = editingAttribute.is_system
          ? { color: formData.color, icon: formData.icon || undefined }
          : { name: formData.name, description: formData.description || undefined, color: formData.color, icon: formData.icon || undefined };

        await updateAttribute(editingAttribute.id, updateData);
        setEditingAttribute(null);
      }
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : t('common:error.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (attributeId: string) => {
    const attr = attributes.find(a => a.id === attributeId);
    if (!attr || attr.is_system) return;

    if (!confirm(t('flights:attributes.confirmDelete', { name: attr.name }))) {
      return;
    }

    try {
      await deleteAttribute(attributeId);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : t('flights:attributes.deleteFailed'));
    }
  };

  const cancelEdit = () => {
    setEditingAttribute(null);
    setIsCreating(false);
    setLocalError(null);
    clearError();
  };

  if (!isOpen) return null;

  const systemAttributes = attributes.filter(a => a.is_system);
  const customAttributes = attributes.filter(a => !a.is_system);
  const canCreateMore = attributes.length < 14;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              {t('flights:attributes.manageTitle')}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {t('flights:attributes.usageCount', { count: attributes.length, max: 14 })}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Error Display */}
          {(error || localError) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
              <span>{error || localError}</span>
              <button onClick={() => { clearError(); setLocalError(null); }} className="text-red-500 hover:text-red-700">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Create/Edit Form */}
          {(isCreating || editingAttribute) && (
            <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {isCreating ? t('flights:attributes.createNew') : t('flights:attributes.editAttribute', { name: editingAttribute?.name })}
              </h3>

              <div className="space-y-3">
                {/* Name (not editable for system attributes) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:fields.name')}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder={t('flights:attributes.namePlaceholder')}
                    required
                    disabled={editingAttribute?.is_system}
                  />
                  {editingAttribute?.is_system && (
                    <p className="text-xs text-gray-500 mt-1">{t('flights:attributes.systemCannotRename')}</p>
                  )}
                </div>

                {/* Description (not editable for system attributes) */}
                {!editingAttribute?.is_system && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('flights:attributes.descriptionOptional')}</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={t('flights:attributes.descriptionPlaceholder')}
                    />
                  </div>
                )}

                {/* Color */}
                <div>
                  <ColorPicker
                    color={formData.color}
                    onChange={(color) => setFormData(prev => ({ ...prev, color }))}
                    label={t('common:fields.color')}
                  />
                </div>

                {/* Icon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('flights:attributes.iconOptional')}</label>
                  <div className="flex flex-wrap gap-2">
                    {/* None option */}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, icon: '' }))}
                      className={`p-2.5 rounded-lg border transition-colors ${
                        !formData.icon
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-400'
                      }`}
                      title={t('flights:attributes.noIcon')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </button>
                    {ATTRIBUTE_ICONS.map((icon) => (
                      <button
                        key={icon.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, icon: prev.icon === icon.value ? '' : icon.value }))}
                        className={`p-2.5 rounded-lg border transition-colors ${
                          formData.icon === icon.value
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700'
                        }`}
                        title={icon.label}
                      >
                        <AttributeIcon icon={icon.value} className="w-5 h-5" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  {t('common:actions.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.name.trim()}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isSubmitting ? t('common:actions.saving') : isCreating ? t('common:actions.create') : t('common:actions.save')}
                </button>
              </div>
            </form>
          )}

          {/* System Attributes */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              {t('flights:attributes.systemAttributes', { count: systemAttributes.length })}
            </h3>
            <div className="space-y-2">
              {systemAttributes.map((attr) => (
                <AttributeItem
                  key={attr.id}
                  attribute={attr}
                  onEdit={() => setEditingAttribute(attr)}
                  onDelete={() => {}}
                  isEditing={editingAttribute?.id === attr.id}
                />
              ))}
            </div>
          </div>

          {/* Custom Attributes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                {t('flights:attributes.customAttributes', { count: customAttributes.length })}
              </h3>
              {canCreateMore && !isCreating && !editingAttribute && (
                <button
                  onClick={() => setIsCreating(true)}
                  className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('flights:attributes.newAttribute')}
                </button>
              )}
            </div>

            {customAttributes.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <p className="text-sm text-gray-500 mb-3">{t('flights:attributes.noCustomAttributes')}</p>
                {canCreateMore && !isCreating && (
                  <button
                    onClick={() => setIsCreating(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('flights:attributes.createFirst')}
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {customAttributes.map((attr) => (
                  <AttributeItem
                    key={attr.id}
                    attribute={attr}
                    onEdit={() => setEditingAttribute(attr)}
                    onDelete={() => handleDelete(attr.id)}
                    isEditing={editingAttribute?.id === attr.id}
                  />
                ))}
              </div>
            )}
          </div>

          {!canCreateMore && (
            <p className="text-xs text-amber-600 mt-4 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {t('flights:attributes.maxReached')}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
          >
            {t('common:actions.done')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Individual attribute item component
function AttributeItem({
  attribute,
  onEdit,
  onDelete,
  isEditing,
}: {
  attribute: FlightAttribute;
  onEdit: () => void;
  onDelete: () => void;
  isEditing: boolean;
}) {
  const { t } = useTranslation(['flights', 'common']);

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
        isEditing ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      {/* Color indicator with icon */}
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: attribute.color }}
      >
        {attribute.icon ? (
          <span className="text-white">
            <AttributeIcon icon={attribute.icon} className="w-4 h-4" />
          </span>
        ) : attribute.is_system ? (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        ) : (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{attribute.name}</span>
          {attribute.is_system && (
            <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">{t('flights:attributes.system')}</span>
          )}
          <span className="text-xs text-gray-400 font-mono">{attribute.slug}</span>
        </div>
        {attribute.description && (
          <p className="text-sm text-gray-500 truncate">{attribute.description}</p>
        )}
        {attribute.flight_count !== undefined && attribute.flight_count > 0 && (
          <span className="text-xs text-gray-400">{t('flights:attributes.flightCount', { count: attribute.flight_count })}</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        <button
          onClick={onEdit}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title={t('common:actions.edit')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        {!attribute.is_system && (
          <button
            onClick={onDelete}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title={t('common:actions.delete')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export default AttributeManager;
